import React, { useState, useRef } from "react";
import toast, { Toaster } from 'react-hot-toast';
import GuestHeader from "../GuestHeader";
import GuestFooter from "../GuestFooter";
import SignatureCanvas from 'react-signature-canvas';
import { insertGiftAidData } from "../GuestApiServices";
import { getGiftAidList } from "../../Admin/sundayupdates/NoticeUpdateService";

const GiftAidContainer = () => {
    // --- State variables to match the PDF form fields ---
    const [title, setTitle] = useState('');
    const [firstName, setFirstName] = useState('');
    const [surname, setSurname] = useState('');
    const [fullAddress, setFullAddress] = useState('');
    const [postcode, setPostcode] = useState('');
    const [email, setEmail] = useState('');
    const [donationAmount, setDonationAmount] = useState('');
    const [date, setDate] = useState('');
    const [declarationChecked, setDeclarationChecked] = useState(false);

    const [signatureDataURL, setSignatureDataURL] = useState(null);
    const signatureRef = useRef(null);

    // --- Validation Logic ---
    const validateForm = () => {
        // Required fields: Title, First Name, Surname, Address, Postcode, Donation Amount, Date, Declaration, and Email
        if (!title || !firstName || !surname || !fullAddress || !postcode || !donationAmount || !date || !declarationChecked || !email) {
            toast.error('Please fill in all required fields and tick the declaration.');
            return false;
        }

        // Basic check for postcode
        if (!postcode.trim()) {
            toast.error('Please enter your Postcode.');
            return false;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            toast.error('Please enter a valid email address.');
            return false;
        }

        // Basic check for donation amount to be a positive number
        const amount = parseFloat(donationAmount);
        if (isNaN(amount) || amount < 0) {
            toast.error('Please enter a valid donation amount greater than zero.');
            return false;
        }

        return true;
    };

    const clearSignature = () => {
        if (signatureRef.current) {
            signatureRef.current.clear();
            setSignatureDataURL(null);
        }
    };

    // Saves the signature data to state when drawing stops
    const handleEnd = () => {
        if (signatureRef.current && !signatureRef.current.isEmpty()) {
            const dataURL = signatureRef.current.toDataURL('image/png');
            setSignatureDataURL(dataURL);
        } else {
            setSignatureDataURL(null);
        }
    };

    // --- Submission Handler ---
    const handleSubmit = (event) => {
        event.preventDefault();
        if (validateForm()) {
            let data = {
                title,
                firstName,
                surname,
                fullAddress,
                postcode,
                donationAmount,
                date,
                email,
                declarationChecked,
                churchName: "Mor Baselios Jacobite Syrian Orthodox Church Derby",
                charityNumber: "1213220",
                signature: signatureDataURL
            };

            console.log("Form data submitted:", data);

            const loadingToast = toast.loading('Submitting Gift Aid Declaration...');

            insertGiftAidData(data).then((response) => {
                toast.dismiss(loadingToast);
                toast.success(response.data.message || 'Gift Aid Declaration submitted successfully!');

                // Reset form
                setTitle('');
                setFirstName('');
                setSurname('');
                setFullAddress('');
                setPostcode('');
                setDonationAmount('');
                setDate('');
                setEmail('');
                setDeclarationChecked(false);
                clearSignature();

            }).catch((error) => {
                toast.dismiss(loadingToast);
                toast.error('Failed to submit Gift Aid Declaration. Please try again.');
            })
        }
    };

    const onEmailBlur = () => {
        if (!email) return;

        let data = {
            email: email
        }

        getGiftAidList(data).then((response) => {
            if (response.data.success) {
                if (response.data.data) {
                    if (response.data.data.length > 0) {
                        toast.error("Data already submitted for this user");
                    }
                }
            }
        }).catch((error) => {
            console.log("Error checking email:", error);
        })
    }

    return (
        <>
            <GuestHeader />

            <div className="content">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-10 col-xl-8">
                            <h2 className="mb-4 text-center">Gift Aid Declaration Form</h2>
                            
                            {/* Information text from the PDF */}
                            <div className="px-3 px-md-4 px-lg-5 mb-4">
                                <p className="mb-2"><strong>Boost your donation by 25p of Gift Aid for every £1 you donate.</strong></p>
                                <p className="mb-0 text-muted small">
                                    Gift Aid is reclaimed by <strong>Mor Baselios Jacobite Syrian Orthodox Church Derby</strong> (Charity Registration Number: <strong>1213220</strong>) from the tax you pay for the current tax year. Your address is needed to identify you as a current UK taxpayer.
                                </p>
                            </div>
                        </div>
                        
                        <div className="col-12 col-lg-10 col-xl-8">
                            <div className="card shadow-sm p-3 p-md-4 p-lg-5">
                                <form onSubmit={handleSubmit} id="giftAidForm">
                                    {/* Gift Aid Declaration - Top Section */}
                                    <h4 className="mb-3">Gift Aid Declaration</h4>
                                    <div className="row mb-3">
                                        <div className="col-12 form-group">
                                            <label htmlFor="donationAmount" className="form-label small fw-semibold">
                                                I want to Gift Aid my donation of £ (and any donations I make in the future or have made in the past 4 years to Mor Baselios Jacobite Syrian Orthodox Church Derby):
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className="form-control form-control-lg"
                                                name="donationAmount"
                                                id="donationAmount"
                                                placeholder="Enter amount"
                                                value={donationAmount}
                                                onChange={(e) => setDonationAmount(e.target.value)}
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Your Details Section */}
                                    <h4 className="mb-3 mt-4">Your Details</h4>
                                    <div className="row g-3">
                                        <div className="col-12 col-sm-4 form-group">
                                            <label htmlFor="title" className="form-label small fw-semibold">Title:</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="title"
                                                id="title"
                                                value={title}
                                                onChange={(e) => setTitle(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="col-12 col-sm-8 form-group">
                                            <label htmlFor="firstName" className="form-label small fw-semibold">First name(s):</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="firstName"
                                                id="firstName"
                                                value={firstName}
                                                onChange={(e) => setFirstName(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="col-12 form-group">
                                            <label htmlFor="surname" className="form-label small fw-semibold">Surname:</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="surname"
                                                id="surname"
                                                value={surname}
                                                onChange={(e) => setSurname(e.target.value)}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="row mt-2">
                                        <div className="col-12 form-group">
                                            <label htmlFor="fullAddress" className="form-label small fw-semibold">Full home address:</label>
                                            <textarea
                                                className="form-control"
                                                name="fullAddress"
                                                id="fullAddress"
                                                rows={3}
                                                value={fullAddress}
                                                onChange={(e) => setFullAddress(e.target.value)}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="row g-3 mt-1">
                                        <div className="col-12 col-sm-6 form-group">
                                            <label htmlFor="postcode" className="form-label small fw-semibold">Postcode:</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="postcode"
                                                id="postcode"
                                                value={postcode}
                                                onChange={(e) => setPostcode(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="col-12 col-sm-6 form-group">
                                            <label htmlFor="date" className="form-label small fw-semibold">Date:</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="date"
                                                id="date"
                                                value={date}
                                                onChange={(e) => setDate(e.target.value)}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="row mt-2">
                                        <div className="col-12 col-sm-8 form-group">
                                            <label htmlFor="email" className="form-label small fw-semibold">Email Id:</label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                name="email"
                                                id="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                onBlur={onEmailBlur}
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Signature Section */}
                                    <h4 className="mb-3 mt-4">Signature</h4>
                                    <div className="row mb-3">
                                        <div className="col-12 form-group border rounded p-3">
                                            <label className="form-label small fw-semibold d-block">Please sign below:</label>

                                            {/* SignaturePad Component */}
                                            <div style={{ 
                                                border: '1px solid #ddd', 
                                                minHeight: '120px', 
                                                width: '100%', 
                                                marginBottom: '10px',
                                                borderRadius: '4px',
                                                backgroundColor: '#f8f9fa'
                                            }}>
                                                <SignatureCanvas
                                                    ref={signatureRef}
                                                    canvasProps={{ 
                                                        width: 500, 
                                                        height: 120, 
                                                        className: 'sigCanvas w-100',
                                                        style: { width: '100%' }
                                                    }}
                                                    onEnd={handleEnd}
                                                    style={{ width: '100%', height: '120px' }}
                                                />
                                            </div>

                                            {/* Signature Controls */}
                                            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-sm mb-2 mb-sm-0 me-sm-3"
                                                    onClick={clearSignature}
                                                >
                                                    Clear Signature
                                                </button>
                                                {signatureDataURL ? (
                                                    <small className="text-success">
                                                        ✓ Signature Captured (Click 'Clear' to re-sign)
                                                    </small>
                                                ) : (
                                                    <small className="text-danger">
                                                        ✗ Signature required before submission
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Declaration Section - FIXED CHECKBOX */}
                                    <h4 className="mb-3 mt-4">Declaration</h4>
                                    <div className="row">
                                        <div className="col-12">
                                            <div className="border rounded p-3 bg-light">
                                                <div className="form-group form-check m-0">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input me-2"
                                                        id="declarationCheck"
                                                        checked={declarationChecked}
                                                        onChange={(e) => setDeclarationChecked(e.target.checked)}
                                                        required
                                                    />
                                                    <label className="form-check-label small" htmlFor="declarationCheck">
                                                        I am a UK taxpayer and understand that if I pay less Income Tax and/or Capital Gains Tax than the amount of Gift Aid claimed on all my donations in that tax year, it is my responsibility to pay any difference.
                                                    </label>
                                                </div>
                                                <p className="mt-2 mb-0 text-muted small">
                                                    *If you pay Income Tax at the higher or additional rate and want to receive the additional tax relief due to you, you must include all your Gift Aid donations on your Self-Assessment tax return or ask HMRC to adjust your tax code.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row mt-4">
                                        <div className="col-12 text-center">
                                            <button 
                                                type="submit" 
                                                className="btn btn-primary btn-lg w-100 w-sm-auto px-4 py-2"
                                            >
                                                Submit Gift Aid Declaration
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <GuestFooter />
        </>
    );
}

export default GiftAidContainer;